/**
 * BPM Forfaitaire Afschrijvingstabel Calculator
 *
 * Computes Rest-BPM (te betalen BPM) from Bruto BPM using the Dutch
 * forfaitaire depreciation table with all official rules.
 *
 * @see https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/zakelijk/auto_en_vervoer/bpm/
 */

export type VehicleKind = 'PERSONENAUTO' | 'BESTELAUTO';

export interface BpmCalcInput {
  dateFirstUse: string; // ISO date (YYYY-MM-DD)
  dateRdwApproval: string; // ISO date (YYYY-MM-DD)
  vehicleKind: VehicleKind;
  grossBpm: number; // € (Bruto BPM)
}

export interface BpmBand {
  name: string;
  minMonths: number;
  maxMonthsExclusive: number | null; // null = no upper limit
  basePct: number;
  perMonthPct: number;
}

export interface BpmCalcResult {
  depreciationPct: number; // 0..100 (two decimals)
  restBpm: number; // € (rounded to cents)
  monthsTotal: number;
  band: {
    name: string;
    basePct: number;
    perMonthPct: number;
    monthsInBand: number;
  };
  cappedBy5YearRule: boolean; // for BESTELAUTO pre-2025 only
}

/**
 * Forfaitaire depreciation bands for persons cars and motors
 */
const BANDS: BpmBand[] = [
  { name: '0 - <1 maand', minMonths: 0, maxMonthsExclusive: 1, basePct: 0, perMonthPct: 12 },
  { name: '1 - <3 maanden', minMonths: 1, maxMonthsExclusive: 3, basePct: 12, perMonthPct: 4 },
  { name: '3 - <5 maanden', minMonths: 3, maxMonthsExclusive: 5, basePct: 20, perMonthPct: 3.5 },
  { name: '5 - <9 maanden', minMonths: 5, maxMonthsExclusive: 9, basePct: 27, perMonthPct: 1.5 },
  { name: '9 - <18 maanden', minMonths: 9, maxMonthsExclusive: 18, basePct: 33, perMonthPct: 1.0 },
  {
    name: '18 - <30 maanden',
    minMonths: 18,
    maxMonthsExclusive: 30,
    basePct: 42,
    perMonthPct: 0.75,
  },
  {
    name: '30 - <42 maanden',
    minMonths: 30,
    maxMonthsExclusive: 42,
    basePct: 51,
    perMonthPct: 0.5,
  },
  {
    name: '42 - <54 maanden',
    minMonths: 42,
    maxMonthsExclusive: 54,
    basePct: 57,
    perMonthPct: 0.42,
  },
  {
    name: '54 - <66 maanden',
    minMonths: 54,
    maxMonthsExclusive: 66,
    basePct: 62,
    perMonthPct: 0.42,
  },
  {
    name: '66 - <78 maanden',
    minMonths: 66,
    maxMonthsExclusive: 78,
    basePct: 67,
    perMonthPct: 0.42,
  },
  {
    name: '78 - <90 maanden',
    minMonths: 78,
    maxMonthsExclusive: 90,
    basePct: 72,
    perMonthPct: 0.25,
  },
  {
    name: '90 - <102 maanden',
    minMonths: 90,
    maxMonthsExclusive: 102,
    basePct: 75,
    perMonthPct: 0.25,
  },
  {
    name: '102 - <114 maanden',
    minMonths: 102,
    maxMonthsExclusive: 114,
    basePct: 78,
    perMonthPct: 0.25,
  },
  {
    name: '≥114 maanden',
    minMonths: 114,
    maxMonthsExclusive: null,
    basePct: 81,
    perMonthPct: 0.19,
  },
];

/**
 * Check if a date is the last day of its month
 */
function isLastDayOfMonth(d: Date): boolean {
  const nextDay = new Date(d);
  nextDay.setDate(d.getDate() + 1);
  return nextDay.getMonth() !== d.getMonth();
}

/**
 * Round to cents (2 decimal places)
 */
function roundToCents(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Calculate months elapsed between two dates with partial month counting.
 *
 * Uses UTC date components throughout to avoid timezone-induced day shifts.
 *
 * Rules:
 * - Any partial month counts as a full month.
 * - Special case: if start is the last day of its month AND end is the last day
 *   of a shorter month (e.g. Jan 31 → Feb 28), this counts as exactly 1 full
 *   month with no partial remainder.
 *
 * @returns Integer months elapsed (partial months round up to full)
 */
export function diffMonthsWithPartial(startISO: string, endISO: string): number {
  const start = new Date(startISO);
  const end = new Date(endISO);

  if (end <= start) {
    return 0;
  }

  const startY = start.getUTCFullYear();
  const startM = start.getUTCMonth();
  const startD = start.getUTCDate();
  const endY = end.getUTCFullYear();
  const endM = end.getUTCMonth();
  const endD = end.getUTCDate();

  // Full calendar months between start and end (may be one too many if day hasn't arrived)
  let months = (endY - startY) * 12 + (endM - startM);

  if (endD < startD) {
    // The day-of-month hasn't been reached yet in the final month.
    // Special case: both dates are the last day of their respective months
    // (e.g. Jan 31 → Feb 28) — treat as a complete month, no adjustment needed.
    const startLastDay = new Date(Date.UTC(startY, startM + 1, 0)).getUTCDate();
    const endLastDay = new Date(Date.UTC(endY, endM + 1, 0)).getUTCDate();
    const bothLastDays = startD === startLastDay && endD === endLastDay;
    if (!bothLastDays) {
      months--; // Haven't completed the last calendar month yet
    }
  }

  // Check whether there is a partial month remaining after 'months' full months.
  // Compute the boundary = start + months months, clamping day to month's last day.
  const boundaryLastDay = new Date(Date.UTC(startY, startM + months + 1, 0)).getUTCDate();
  const boundaryD = Math.min(startD, boundaryLastDay);
  const boundaryMs = Date.UTC(startY, startM + months, boundaryD);

  if (boundaryMs < end.getTime()) {
    // There is a partial month after the full months — counts as a full month.
    months++;
  }

  return months;
}

/**
 * Find the appropriate depreciation band for the given months
 */
function findBand(months: number): BpmBand {
  for (const band of BANDS) {
    if (
      months >= band.minMonths &&
      (band.maxMonthsExclusive === null || months < band.maxMonthsExclusive)
    ) {
      return band;
    }
  }
  // Fallback to last band (should never happen with proper data)
  return BANDS[BANDS.length - 1];
}

/**
 * Calculate depreciation percentage based on dates and vehicle kind
 */
export function calcDepreciationPct(input: {
  dateFirstUse: string;
  dateRdwApproval: string;
  vehicleKind: VehicleKind;
}): {
  depreciationPct: number;
  monthsTotal: number;
  band: {
    name: string;
    basePct: number;
    perMonthPct: number;
    monthsInBand: number;
  };
  cappedBy5YearRule: boolean;
} {
  const { dateFirstUse, dateRdwApproval, vehicleKind } = input;

  // Calculate total months elapsed
  const monthsTotal = diffMonthsWithPartial(dateFirstUse, dateRdwApproval);

  // Find the appropriate band
  const bandData = findBand(monthsTotal);

  // Calculate months within this band
  const monthsInBand = monthsTotal - bandData.minMonths;

  // Calculate depreciation percentage
  let depreciationPct = bandData.basePct + monthsInBand * bandData.perMonthPct;

  // Check for BESTELAUTO 5-year rule
  let cappedBy5YearRule = false;
  if (vehicleKind === 'BESTELAUTO') {
    const firstUseDate = new Date(dateFirstUse);
    const cutoffDate = new Date('2024-12-31');

    // If first use ≤ 2024-12-31 and ≥ 60 months (5 years), depreciation = 100%
    if (firstUseDate <= cutoffDate && monthsTotal >= 60) {
      depreciationPct = 100;
      cappedBy5YearRule = true;
    }
  }

  // Cap at 100%
  depreciationPct = Math.min(100, depreciationPct);

  // Round to 2 decimals
  depreciationPct = roundToCents(depreciationPct);

  return {
    depreciationPct,
    monthsTotal,
    band: {
      name: bandData.name,
      basePct: bandData.basePct,
      perMonthPct: bandData.perMonthPct,
      monthsInBand,
    },
    cappedBy5YearRule,
  };
}

/**
 * Calculate Rest-BPM from Gross BPM and depreciation percentage
 */
export function calcRestBpm(grossBpm: number, depreciationPct: number): number {
  const restBpm = (grossBpm * (100 - depreciationPct)) / 100;
  return roundToCents(restBpm);
}

/**
 * Complete BPM calculation
 *
 * @param input - Vehicle and BPM data
 * @returns Complete calculation result with depreciation and rest BPM
 */
export function calculateBpm(input: BpmCalcInput): BpmCalcResult {
  const { dateFirstUse, dateRdwApproval, vehicleKind, grossBpm } = input;

  // Calculate depreciation
  const depreciationResult = calcDepreciationPct({
    dateFirstUse,
    dateRdwApproval,
    vehicleKind,
  });

  // Calculate rest BPM
  const restBpm = calcRestBpm(grossBpm, depreciationResult.depreciationPct);

  return {
    depreciationPct: depreciationResult.depreciationPct,
    restBpm,
    monthsTotal: depreciationResult.monthsTotal,
    band: depreciationResult.band,
    cappedBy5YearRule: depreciationResult.cappedBy5YearRule,
  };
}
